import { Button } from "azure-devops-ui/Button";
import { TextField } from "azure-devops-ui/TextField";
import { css } from "azure-devops-ui/Util";
import React from "react";
import { EventContext } from "../../../common/contexts/event";
import { NavigationContext } from "../../../common/contexts/navigation";
import { useFocusTracker } from "../../../common/hooks/usefocustracker";
import { useSubscription } from "../../../common/hooks/useobservable";
import { useTimeout } from "../../../common/hooks/usetimeout";
import { SearchContext } from "../../contexts/search";
import { UserPreferencesContext } from "../../contexts/userpreferences";
import * as Icons from "../illustration/icons";
import { ISearchDropdown, SearchDropdown } from "./searchdropdown";

import "./searchform.css";

const { ClearSearchButton, PreviewLabel, SearchButton } = window.Resources.SearchInput;

export interface ISearchForm {
  updateLayout: () => void;
}

export interface ISearchFormProps {
  /**
   * The string to initialize the search input with. This may be an applied
   * search, or a recommendation, etc.
   */
  activeSearch?: string;

  /**
   * Custom css className added to the root of the search form.
   */
  className?: string;

  /**
   * The seach form component offers an API through the componentRef prop. If
   * the caller wishes to access this API it should supply a reference.
   */
  componentRef?: React.MutableRefObject<ISearchForm | undefined>;

  /**
   * Should the search dropdown be available to the user.
   *
   * @default false
   */
  enableDropdown?: boolean;

  /**
   * Placeholder text for the search input.
   */
  placeholder?: string;
}

export function SearchForm(props: ISearchFormProps): React.ReactElement {
  const { activeSearch = "", className, componentRef, enableDropdown = false, placeholder } = props;

  const eventContext = React.useContext(EventContext);
  const navigationContext = React.useContext(NavigationContext);
  const searchContext = React.useContext(SearchContext);
  const userPreferencesContext = React.useContext(UserPreferencesContext);

  const formElement = React.useRef<HTMLFormElement>(null);
  const inputElement = React.useRef<HTMLTextAreaElement & HTMLInputElement>(null);
  const searchDropdown = React.useRef<ISearchDropdown>();

  const [searchText, setSearchText] = React.useState(activeSearch || "");
  const [showDropdown, setShowDropdown] = React.useState(false);

  const { setTimeout: setCollapseTimeout } = useTimeout();
  const { setTimeout: setFocusTimeout } = useTimeout();
  const { setTimeout: setExpandTimeout } = useTimeout();

  const { onBlur, onFocus } = useFocusTracker({
    onFocusChange: (hasFocus) => {
      setExpandTimeout(() => setShowDropdown(enableDropdown && hasFocus), 0);
    }
  });

  const photoPreferences = useSubscription(userPreferencesContext.getUserPreferences("photo"));

  React.useImperativeHandle(componentRef, () => ({ updateLayout }));

  React.useEffect(() => {
    inputElement.current?.setAttribute("name", "q");
    inputElement.current?.setAttribute("enterkeyhint", "search");
  }, []);

  return (
    <form
      action="/search"
      className={css(className, "relative search-form flex-row flex-justify-center flex-grow", searchText && "search-term-available")}
      onBlur={onBlur}
      onClick={(event) => {
        if (!event.defaultPrevented) {
          inputElement.current?.focus();
          setExpandTimeout(() => setShowDropdown(enableDropdown), 0);
        }
      }}
      onFocus={onFocus}
      onKeyDown={(event) => {
        if (!event.defaultPrevented && enableDropdown) {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            setShowDropdown(true);
            event.preventDefault();

            // We need to delay the focus given that some of the scenarios
            // require the combobox to open first.
            setFocusTimeout(() => searchDropdown.current?.focus({ item: event.key === "ArrowDown" ? "first" : "last" }), 0);
          } else if (event.key === "Escape") {
            setShowDropdown(false);
          }
        }
      }}
      ref={formElement}
      role="search"
      method="get"
    >
      <TextField
        ariaControls={showDropdown ? "search-dropdown" : undefined}
        ariaExpanded={enableDropdown ? showDropdown : undefined}
        ariaHasPopup={enableDropdown ? true : undefined}
        className="search-text-field overflow-hidden"
        containerClassName="search-input flex-grow body-m overflow-hidden"
        inputClassName="overflow-hidden"
        inputElement={inputElement}
        onChange={(_event, value) => setSearchText(value)}
        onKeyDown={(event) => {
          if (!event.defaultPrevented) {
            // Using deprecated keyCode because enter is not capturing Go on mobile.
            if (event.key === "Enter") {
              if (searchText) {
                performSearch(event, searchText, "keyboard");
              }

              // Need to prevent default even without text to prevent form submission.
              event.preventDefault();
            }
          }
        }}
        placeholder={placeholder}
        prefixIconProps={{ render: (className) => <Icons.Search className={css(className, "flex-noshrink small")} /> }}
        role={enableDropdown ? "combobox" : undefined}
        suffixIconProps={{
          render: () => (
            <>
              <Button
                ariaLabel={ClearSearchButton}
                className={css("input-button", !searchText && "hidden")}
                iconProps={{ render: (className) => <Icons.Clear className={className} /> }}
                onClick={(event) => {
                  eventContext.dispatchEvent("telemetryAvailable", { action: "userAction", name: "clearSearch" });
                  setSearchText("");
                  inputElement.current?.focus();
                  event.preventDefault();
                }}
                subtle={true}
              />
              <Button
                ariaLabel={SearchButton}
                className={css("input-button search-arrow-button", !searchText && "hidden")}
                iconProps={{ render: (className) => <Icons.ArrowForward className={css("xsmall margin-4", className)} /> }}
                onClick={(event) => performSearch(event, searchText, "button")}
                primary={true}
              />
              {enableDropdown ? <span className="search-preview-label margin-horizontal-16 body-s">{PreviewLabel}</span> : null}
            </>
          )
        }}
        value={searchText}
      />
      {showDropdown ? (
        <SearchDropdown
          anchorElement={formElement}
          clearSearch={(searchTerm) => (searchTerm ? searchContext.removeSearch(searchTerm) : searchContext.clearSearches())}
          comboboxElement={inputElement}
          componentRef={searchDropdown}
          executeSearch={(value: string, mode: string) => performSearch(undefined, value, mode)}
          listId="search-dropdown"
          inspirations={searchContext.getSuggestions()}
          onDismiss={() => setShowDropdown(false)}
          recentSearches={searchContext.getSearches()}
        />
      ) : null}
    </form>
  );

  function performSearch(event: React.KeyboardEvent | React.MouseEvent | undefined, value: string, mode: string) {
    // We don't want any spacing at the start or end of the search terms.
    value = value.trim();

    // Update the component to show the current text and hide the dropdown when
    // a search is performed.
    setSearchText(value);

    // We need to delay the collapse since this will force focus to the dropdown
    // re-opening the dropdown after it closes.
    setCollapseTimeout(() => setShowDropdown(false), 1);

    // Just ignore the search if it is the current search.
    if (value.localeCompare(activeSearch, undefined, { sensitivity: "base" }) !== 0) {
      // Save this search in the search context.
      searchContext.addSearch(value);

      navigationContext.navigate(event, "navigateSearch", {
        href: navigationContext.prepare(`/search?${new URLSearchParams({ q: value })}`),
        telemetryProperties: {
          florenceSearch: photoPreferences?.florenceSearch || false,
          mode
        },
        useReplace: activeSearch ? true : false
      });
    }
  }

  function updateLayout() {
    searchDropdown.current?.updateLayout();
  }
}
